/**
 * File downloads.
 *
 * A blob URL plus a synthetic click covers every format we produce, so there is no
 * reason to pull in a dependency for it. The URL is revoked on the next tick — revoking
 * it synchronously would race the browser's own fetch of the blob in some engines.
 */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function downloadText(text: string, filename: string, mime: string): void {
  downloadBlob(new Blob([text], { type: `${mime};charset=utf-8` }), filename);
}

export type RasterFormat = 'png' | 'jpeg' | 'webp';

export const RASTER_MIME: Record<RasterFormat, string> = {
  png: 'image/png',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
};

/** Converts a canvas of either flavour to a Blob. */
export async function canvasToBlob(
  canvas: OffscreenCanvas | HTMLCanvasElement,
  format: RasterFormat,
  quality: number,
): Promise<Blob> {
  const type = RASTER_MIME[format];
  if (canvas instanceof OffscreenCanvas) {
    return canvas.convertToBlob({ type, quality });
  }
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Canvas konnte nicht kodiert werden.'))),
      type,
      quality,
    );
  });
}

/** Fills in a filename template such as `{index}_{technique}_{word}_{seed}`. */
export function formatFilename(template: string, tokens: Record<string, string | number>): string {
  const replaced = template.replace(/\{(\w+)\}/g, (_, key: string) =>
    key in tokens ? String(tokens[key]) : `{${key}}`,
  );
  // Strip characters that are illegal in filenames on at least one major platform.
  return replaced.replace(/[\\/:*?"<>|]+/g, '-').replace(/\s+/g, ' ').trim() || 'export';
}
